"use client"

import { useState } from "react"
import { Meeting } from "@/schema"
import { Button } from "@ui/shad"
import { Plus } from "lucide-react"
import { toast } from "sonner"

import { ALL_BOOKS_DISABLED_TOOLTIP, isAllBooksId } from "@/lib/all-books"
import { useUpdateMeeting } from "@/hooks/api/meetings"
import { useBookId } from "@/hooks/use-book-id"
import { CrossReferenceSelector } from "@/components/cross-reference-selector"
import PeekView from "@/components/peek-view"
import { UserAvatar } from "@/components/users/user-avatar"

interface AssociationsSelectorProps {
  meeting: Meeting
}

export default function AssociationsSelector({ meeting }: AssociationsSelectorProps) {
  const [selectorOpen, setSelectorOpen] = useState(false)
  const updateMeeting = useUpdateMeeting()
  const bookId = useBookId()
  const inAllBooksMode = isAllBooksId(bookId)

  const households = meeting.households ?? []
  const contacts = meeting.contacts ?? []
  const businesses = meeting.businesses ?? []
  const meetingUsers = meeting.meeting_users ?? []

  const handleSelect = (value: { type: string; id: string; label: string } | null) => {
    if (!value) return
    setSelectorOpen(false)

    if (value.type === "Household") {
      const currentIds = households.map((h) => h.id)
      if (currentIds.includes(value.id)) return
      updateMeeting.mutate(
        { meetingId: meeting.id, data: { household_ids: [...currentIds, value.id] } },
        { onSuccess: () => toast.success(`Added ${value.label}`) }
      )
    } else if (value.type === "Contact") {
      const currentIds = contacts.map((c) => c.id)
      if (currentIds.includes(value.id)) return
      updateMeeting.mutate(
        { meetingId: meeting.id, data: { contact_ids: [...currentIds, value.id] } },
        { onSuccess: () => toast.success(`Added ${value.label}`) }
      )
    } else if (value.type === "Business") {
      const currentIds = businesses.map((b) => b.id)
      if (currentIds.includes(value.id)) return
      updateMeeting.mutate(
        { meetingId: meeting.id, data: { business_ids: [...currentIds, value.id] } },
        { onSuccess: () => toast.success(`Added ${value.label}`) }
      )
    } else if (value.type === "User") {
      const currentIds = meetingUsers.map((u) => u.id)
      if (currentIds.includes(value.id)) return
      updateMeeting.mutate(
        { meetingId: meeting.id, data: { user_ids: [...currentIds, value.id] } },
        { onSuccess: () => toast.success(`Added ${value.label}`) }
      )
    }
  }

  const handleRemoveHousehold = (householdId: string) => {
    const removed = households.find((h) => h.id === householdId)
    const remainingIds = households.filter((h) => h.id !== householdId).map((h) => h.id)
    updateMeeting.mutate(
      { meetingId: meeting.id, data: { household_ids: remainingIds } },
      { onSuccess: () => toast.success(`Removed ${removed?.name ?? "household"}`) }
    )
  }

  const handleRemoveContact = (contactId: string) => {
    const removed = contacts.find((c) => c.id === contactId)
    const remainingIds = contacts.filter((c) => c.id !== contactId).map((c) => c.id)
    updateMeeting.mutate(
      { meetingId: meeting.id, data: { contact_ids: remainingIds } },
      { onSuccess: () => toast.success(`Removed ${removed?.name ?? "contact"}`) }
    )
  }

  const handleRemoveBusiness = (businessId: string) => {
    const removed = businesses.find((b) => b.id === businessId)
    const remainingIds = businesses.filter((b) => b.id !== businessId).map((b) => b.id)
    updateMeeting.mutate(
      { meetingId: meeting.id, data: { business_ids: remainingIds } },
      { onSuccess: () => toast.success(`Removed ${removed?.name ?? "business"}`) }
    )
  }

  const handleRemoveUser = (userId: string) => {
    const remainingIds = meetingUsers.filter((u) => u.id !== userId).map((u) => u.id)
    updateMeeting.mutate(
      { meetingId: meeting.id, data: { user_ids: remainingIds } },
      { onSuccess: () => toast.success("Removed user") }
    )
  }

  const hasAssociations =
    households.length > 0 || contacts.length > 0 || businesses.length > 0 || meetingUsers.length > 0

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {!hasAssociations && <span className="text-muted-foreground text-sm">No associations</span>}
        {households.map((household) => (
          <div key={household.id} className="group flex items-center">
            <PeekView
              label={household.name}
              recordType={household.type}
              recordId={household.id}
              asPill
              onRemove={() => handleRemoveHousehold(household.id)}
            />
          </div>
        ))}
        {contacts.map((contact) => (
          <div key={contact.id} className="group flex items-center">
            <PeekView
              label={contact.name}
              recordType="Contact"
              recordId={contact.id}
              asPill
              onRemove={() => handleRemoveContact(contact.id)}
            />
          </div>
        ))}
        {businesses.map((business) => (
          <div key={business.id} className="group flex items-center">
            <PeekView
              label={business.name}
              recordType="Business"
              recordId={business.id}
              asPill
              onRemove={() => handleRemoveBusiness(business.id)}
            />
          </div>
        ))}
        {meetingUsers.map((user) => (
          <div key={user.id} className="group flex items-center">
            <UserAvatar userId={user.id} size="xs" showName variant="pill" onRemove={handleRemoveUser} />
          </div>
        ))}
      </div>
      <Button
        variant="ghost"
        size="iconSm"
        className="text-muted-foreground hover:text-foreground size-5!"
        disabled={updateMeeting.isPending || inAllBooksMode}
        title={inAllBooksMode ? ALL_BOOKS_DISABLED_TOOLTIP : undefined}
        onClick={() => setSelectorOpen(true)}
        aria-label="Add association"
      >
        <Plus />
      </Button>
      <CrossReferenceSelector
        inline
        open={selectorOpen}
        onOpenChange={setSelectorOpen}
        onChange={handleSelect}
        allowedTypes={["Household", "Contact", "Business", "User"]}
        bookIdOverride={meeting.book_id}
      />
    </div>
  )
}
